import { PortfolioLoan } from './types';
import { isMockServices } from 'src/utils';
import mock from 'src/services/utils/mock';
import { QueryFunction } from '@tanstack/react-query';
import { z } from 'zod';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';
import { getLoansMockData } from './getLoans';

// ag-grid filter model types
interface BaseFilterModel {
  source?: string;
  fieldId?: string;
  jsonPath?: string;
}

interface TextFilterModel extends BaseFilterModel {
  filterType: 'text';
  type?:
    | 'contains'
    | 'notContains'
    | 'equals'
    | 'notEqual'
    | 'startsWith'
    | 'endsWith';
  filter?: string;
}

interface NumberFilterModel extends BaseFilterModel {
  filterType: 'number';
  type?:
    | 'equals'
    | 'notEqual'
    | 'lessThan'
    | 'lessThanOrEqual'
    | 'greaterThan'
    | 'greaterThanOrEqual';
  filter?: number;
}

interface SetFilterModel extends BaseFilterModel {
  filterType: 'set';
  values?: (string | number)[];
}

export interface SearchLoansSortModelItem {
  colId: string;
  sort: 'asc' | 'desc';
}

// AG Grid "Add condition" emits a combined model.
// AG Grid v31+ uses `conditions` array; legacy uses condition1/condition2.
interface CombinedTextFilterModel extends BaseFilterModel {
  filterType: 'text';
  operator: 'AND' | 'OR';
  condition1?: TextFilterModel;
  condition2?: TextFilterModel;
  conditions?: TextFilterModel[];
}

interface CombinedNumberFilterModel extends BaseFilterModel {
  filterType: 'number';
  operator: 'AND' | 'OR';
  condition1?: NumberFilterModel;
  condition2?: NumberFilterModel;
  conditions?: NumberFilterModel[];
}

export type FilterModel =
  | TextFilterModel
  | NumberFilterModel
  | SetFilterModel
  | CombinedTextFilterModel
  | CombinedNumberFilterModel;
export type FilterModelMap = Record<string, FilterModel>;

export interface SearchLoansParams {
  cursor?: string;
  startRow?: number;
  limit?: number;
  filterModel?: FilterModelMap;
  sortModel?: SearchLoansSortModelItem[];
  fetchAllPages?: boolean;
  /** Used only to scope the React Query cache to the initialized tenant. */
  tenantId?: string;
}

export interface SearchLoansColumn {
  key: string;
  label: string;
  source: string;
  config?: { jsonPath: string; fieldId: string };
}

export interface SearchLoansResponse {
  success: boolean;
  rows: PortfolioLoan[];
  columns?: SearchLoansColumn[];
  nextCursor?: string;
  rowCount?: number;
  totalRowCount?: number;
}

export const SearchLoansResponseSchema = z.object({
  success: z.boolean(),
  rows: z.array(z.any()), // Define proper Loan schema
  columns: z
    .array(
      z.object({
        key: z.string(),
        label: z.string(),
        source: z.string(),
        config: z
          .object({
            jsonPath: z.string(),
            fieldId: z.string(),
          })
          .optional(),
      }),
    )
    .optional(),
  nextCursor: z.string().nullable().optional(),
  rowCount: z.number().int().nonnegative().optional(),
  totalRowCount: z.number().int().nonnegative().optional(),
});

const buildSearchLoansUrl = (params: SearchLoansParams): string => {
  const queryParams: string[] = [];
  if (params.cursor) queryParams.push(`cursor=${params.cursor}`);
  if (typeof params.startRow === 'number')
    queryParams.push(`startRow=${params.startRow}`);
  if (params.limit) queryParams.push(`limit=${params.limit}`);

  // params.tenantId intentionally does not become a URL parameter. The HTTP
  // client resolves tenant routing from the initialized tenant store.

  if (params.filterModel && Object.keys(params.filterModel).length > 0) {
    queryParams.push(
      `filterModel=${encodeURIComponent(JSON.stringify(params.filterModel))}`,
    );
  }

  if (params.sortModel && params.sortModel.length > 0) {
    queryParams.push(
      `sortModel=${encodeURIComponent(JSON.stringify(params.sortModel))}`,
    );
  }

  return `/loans/search${queryParams.length ? '?' + queryParams.join('&') : ''}`;
};

export const fetchSearchLoansPage = async (
  params: SearchLoansParams,
): Promise<SearchLoansResponse> => {
  if (isMockServices()) return await mock(_mockData);

  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<SearchLoansResponse>(
    buildSearchLoansUrl(params),
    {
      method: 'GET',
    },
  );

  if (!response.data) {
    throw new Error('Failed to search loans');
  }

  return SearchLoansResponseSchema.parse({
    success: true,
    rows: response.data?.rows ?? [],
    columns: response.data?.columns ?? [],
    nextCursor: response.data?.nextCursor ?? undefined,
    rowCount: response.data?.rowCount ?? undefined,
    totalRowCount: response.data?.totalRowCount ?? undefined,
  }) as SearchLoansResponse;
};

export const fetchAllSearchLoans = async (
  params: SearchLoansParams,
): Promise<SearchLoansResponse> => {
  const pageLimit = params.limit ?? 100;
  const allRows: PortfolioLoan[] = [];
  let columns: SearchLoansColumn[] = [];

  if (params.sortModel && params.sortModel.length > 0) {
    let startRow = params.startRow ?? 0;
    while (true) {
      const page = await fetchSearchLoansPage({
        ...params,
        cursor: undefined,
        startRow,
        limit: pageLimit,
        fetchAllPages: false,
      });

      if (page.columns?.length) {
        columns = page.columns;
      }

      const rows = page.rows ?? [];
      allRows.push(...rows);
      if (rows.length < pageLimit) break;
      startRow += pageLimit;
    }

    return {
      success: true,
      rows: allRows,
      columns,
      rowCount: allRows.length,
      totalRowCount: allRows.length,
    };
  }

  let cursor = params.cursor;
  const seenCursors = new Set<string | undefined>();

  do {
    if (seenCursors.has(cursor)) break;
    seenCursors.add(cursor);

    const page = await fetchSearchLoansPage({
      ...params,
      cursor,
      limit: pageLimit,
      fetchAllPages: false,
    });

    if (page.columns?.length) {
      columns = page.columns;
    }

    allRows.push(...(page.rows ?? []));
    cursor = page.nextCursor ?? undefined;
  } while (cursor);

  return {
    success: true,
    rows: allRows,
    columns,
    rowCount: allRows.length,
    totalRowCount: allRows.length,
  };
};

const searchLoans: QueryFunction<
  SearchLoansResponse,
  ['/loans/search', SearchLoansParams]
> = async ({ queryKey }) => {
  const [, params] = queryKey;
  return params.fetchAllPages
    ? fetchAllSearchLoans(params)
    : fetchSearchLoansPage(params);
};

export default searchLoans;

// Construct mock data matching the SearchLoansResponse shape to avoid the suspicious double type assertion (unknown as SearchLoansResponse)
const _mockData: SearchLoansResponse = {
  success: getLoansMockData.success,
  rows: getLoansMockData.loans as unknown as PortfolioLoan[],
  nextCursor: getLoansMockData.nextCursor,
  rowCount: getLoansMockData.loans.length,
  totalRowCount: getLoansMockData.loans.length,
};
